import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAlert } from '../Components/AlertContext';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';

import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import {
  CustomInput,
  CustomSelect,
  CustomTextarea,
  CustomToggle,
} from '../Components/CustomFormElements';
import PageLoader from '../Components/PageLoader';
import { isEmpty } from '../utils/validationUtil';

import { AgentApiProvider, useAgentApi } from '../Services/AgentApiProvider';
import { ChannelApiProvider, useChannelApi } from '../Services/ChannelApiProvider';
import {
  PostCallAnalysisApiProvider,
  usePostCallAnalysisApi,
} from '../Services/PostCallAnalysisApiProvider';

import './PostCallAnalysisDetail.css';

const METRIC_TYPE_OPTIONS = [
  { value: 'text', label: 'Text' },
  { value: 'numeric', label: 'Numeric' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'categorical', label: 'Categorical' },
  { value: 'list', label: 'List' },
];

/**
 * Analytics period helper (same behavior as Organization page).
 */

function MetricForm({ initialData, onSubmit, onCancel, isDisabled, showAlert }) {
  const isEdit = !!initialData;

  const [name, setName] = useState(initialData?.name || '');
  const [displayName, setDisplayName] = useState(initialData?.display_name || '');
  const [type, setType] = useState(initialData?.metric_type || 'text');
  const [description, setDescription] = useState(initialData?.description || '');
  const [allowedValues, setAllowedValues] = useState(initialData?.constraints?.values || []);
  const [nullable, setNullable] = useState(initialData?.constraints?.nullable || false);
  const originalValuesCount = initialData?.constraints?.values?.length || 0;

  const handleSubmit = () => {
    if (isEmpty(name) || isEmpty(displayName)) {
      showAlert('fail', 'Please enter metric name and display name');
      return;
    }

    let constraints = null;
    if ((type === 'categorical' || type === 'list') && allowedValues.length > 0) {
      constraints = { values: allowedValues };
    } else if (type === 'numeric') {
      constraints = { nullable: nullable };
    }

    const metricData = {
      name,
      display_name: displayName,
      metric_type: type,
      description,
      constraints,
    };

    onSubmit(metricData);
  };

  return (
    <div
      className="new-metric-form mt-2 mb-2 p-2 border-yes radius-yes"
      style={{ backgroundColor: '#f9f9f9' }}
    >
      <h4 className="h4-title mb-2">{isEdit ? 'Edit Metric' : 'New Custom Metric'}</h4>

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Metric name</div>
          <div className="small-font">Internal name (no spaces)</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomInput
            id="metric-name"
            label="Name"
            placeholder="e.g., customer_effort_score"
            type="text"
            value={name}
            onChange={(id, value) => setName(value)}
            disabled={isDisabled || isEdit}
          />
        </div>
      </div>

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Display name</div>
          <div className="small-font">Human-readable name</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomInput
            id="metric-display-name"
            label="Display Name"
            placeholder="e.g., Customer Effort Score"
            type="text"
            value={displayName}
            onChange={(id, value) => setDisplayName(value)}
            disabled={isDisabled}
          />
        </div>
      </div>

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Metric type</div>
          <div className="small-font">Type of value</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomSelect
            id="metric-type"
            label="Type"
            options={METRIC_TYPE_OPTIONS}
            value={type}
            multiSelect={false}
            onChange={(val) => {
              setType(val);
              if (val !== 'categorical' && val !== 'list') {
                setAllowedValues([]);
              }
              if (val === 'numeric') {
                setNullable(true);
              } else {
                setNullable(false);
              }
            }}
            disabled={isDisabled || isEdit}
          />
        </div>
      </div>

      {(type === 'categorical' || type === 'list') && (
        <div className="input-block d-flex flex-row justify-between">
          <div className="input-description">
            <div className="input-title">Allowed Values</div>
            <div className="small-font">
              {type === 'categorical'
                ? 'Define the possible values for this categorical metric'
                : 'Define the possible items that can appear in this list'}
            </div>
          </div>
          <div className="align-wrapper d-flex flex-column">
            {allowedValues.map((value, index) => {
              const isOriginalValue = index < originalValuesCount;

              return (
                <div key={index} className="d-flex align-center gap mb-2">
                  <CustomInput
                    id={`allowed-value-${index}`}
                    label={`Value ${index + 1}`}
                    placeholder="Enter value"
                    type="text"
                    value={value}
                    onChange={(id, val) => {
                      const updated = [...allowedValues];
                      updated[index] = val;
                      setAllowedValues(updated);
                    }}
                    disabled={isDisabled || isOriginalValue}
                  />
                  {!isDisabled && !isOriginalValue && (
                    <Button
                      variant="danger"
                      size="small"
                      onClick={() => {
                        setAllowedValues(allowedValues.filter((_, i) => i !== index));
                      }}
                    >
                      <i className="fa-solid fa-trash"></i>
                    </Button>
                  )}
                </div>
              );
            })}
            {!isDisabled && (
              <Button
                variant="secondary"
                size="medium"
                onClick={() => setAllowedValues([...allowedValues, ''])}
              >
                <i className="fa-solid fa-plus"></i> Add Value
              </Button>
            )}
          </div>
        </div>
      )}

      {type === 'numeric' && (
        <div className="input-row input-block d-flex flex-row justify-between">
          <div className="input-description">
            <div className="input-title">Nullable</div>
            <div className="small-font">
              Allow agent to return null when the value is not present in the conversation
            </div>
          </div>
          <div className="align-wrapper d-flex align-center">
            <CustomToggle
              id="toggle-metric-nullable"
              label={nullable ? 'Yes' : 'No'}
              checked={nullable}
              onChange={(id, value) => setNullable(value)}
              disabled={isDisabled}
            />
          </div>
        </div>
      )}

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Description</div>
          <div className="small-font">Optional description</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomTextarea
            id="metric-description"
            name="description"
            type="text"
            addedText={description}
            onChange={(value) => setDescription(value)}
            resizable={false}
            height="80px"
            disabled={isDisabled}
          />
        </div>
      </div>

      {!isDisabled && (
        <div className="buttons-container justify-end gap-12 mt-3">
          <Button background="color2" size="medium" onClick={handleSubmit}>
            {isEdit ? 'Save Changes' : 'Add Metric'}
          </Button>
          <Button variant="danger" size="medium" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}

function PostCallAnalysisDetail({ canEdit = true }) {
  const { organizationId, configId } = useParams();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();

  const { getConfigById, createConfig, updateConfig, createMetric, updateMetric, deleteMetric } =
    usePostCallAnalysisApi();

  const { getAgents } = useAgentApi();
  const { getChannels } = useChannelApi();

  const [loading, setLoading] = useState(false);
  const [channelsList, setChannelsList] = useState([]);
  const [isNew, setIsNew] = useState(true);
  const [isDisabled, setIsDisabled] = useState(true);

  // Config fields
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [threadFilter, setThreadFilter] = useState('');
  const [assistantKey, setAssistantKey] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [contextMessageTime, setContextMessageTime] = useState(false);
  const [selectedChannelIds, setSelectedChannelIds] = useState([]);
  const [includeStandardMetrics, setIncludeStandardMetrics] = useState(true);

  // Metrics
  const [metrics, setMetrics] = useState([]);
  const [editingMetricKey, setEditingMetricKey] = useState(null);
  const [showNewMetricForm, setShowNewMetricForm] = useState(false);

  // Assistants list
  const [agents, setAgents] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch agents
      const agentsResponse = await getAgents(0, 100);
      setAgents(agentsResponse.items || []);

      // Fetch channels
      const channelsResponse = await getChannels(0, 500);
      setChannelsList(channelsResponse.items || []);

      if (configId !== 'new') {
        // Fetch config with details
        const config = await getConfigById(configId, true);

        setName(config.name);
        setDescription(config.description || '');
        setThreadFilter(config.properties.thread_filter || '');
        setAssistantKey(config.assistant_key);
        setIsActive(config.is_active);
        setSelectedChannelIds(config.channel_ids || []);
        setMetrics(config.metrics || []);
        setContextMessageTime(config.properties.context_message_time || false);

        setIsNew(false);
        setIsDisabled(!canEdit);
      } else {
        setIsNew(true);
        setIsDisabled(!canEdit);
      }
    } catch (err) {
      console.error(err);
      showAlert('fail', 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [configId, canEdit, getConfigById, getAgents, getChannels]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onCancel = () => {
    navigate(`/organizations/${organizationId}/post-call-analysis`);
  };

  const onSave = async () => {
    if (isEmpty(name)) {
      showAlert('fail', 'Please enter a name');
      return;
    }

    if (isEmpty(assistantKey)) {
      showAlert('fail', 'Please select an assistant');
      return;
    }

    setLoading(true);
    try {
      if (isNew) {
        const data = {
          name,
          description,
          thread_filter: threadFilter,
          assistant_key: assistantKey,
          channel_ids: selectedChannelIds,
          include_standard_metrics: includeStandardMetrics,
          context_message_time: contextMessageTime,
        };

        const newConfig = await createConfig(data);
        showAlert('success', 'Configuration created!');

        navigate(`/organizations/${organizationId}/post-call-analysis/${newConfig.key}`);
      } else {
        const data = {
          name,
          description,
          thread_filter: threadFilter,
          assistant_key: assistantKey,
          is_active: isActive,
          channel_ids: selectedChannelIds,
          context_message_time: contextMessageTime,
        };

        await updateConfig(configId, data);
        showAlert('success', 'Configuration updated!');
      }
    } catch (err) {
      console.error(err);
      showAlert('fail', 'Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  const onAddMetric = async (metricData) => {
    setLoading(true);
    try {
      const newMetric = await createMetric(configId, metricData);
      setMetrics([...metrics, newMetric]);
      setShowNewMetricForm(false);
      showAlert('success', 'Metric added!');
    } catch (err) {
      console.error(err);
      showAlert('fail', 'Failed to add metric');
    } finally {
      setLoading(false);
    }
  };

  const onUpdateMetric = async (metricKey, metricData) => {
    setLoading(true);
    try {
      const updatedMetric = await updateMetric(configId, metricKey, metricData);
      setMetrics(metrics.map((m) => (m.key === metricKey ? { ...m, ...updatedMetric } : m)));
      setEditingMetricKey(null);
      showAlert('success', 'Metric updated!');
    } catch (err) {
      console.error(err);
      showAlert('fail', 'Failed to update metric');
    } finally {
      setLoading(false);
    }
  };

  const onDeleteMetric = (metricKey, isStandard) => {
    if (isStandard) {
      showAlert('fail', 'Cannot delete standard metrics');
      return;
    }

    showConfirmation('Are you sure you want to delete this metric?', async () => {
      setLoading(true);
      try {
        await deleteMetric(configId, metricKey);
        setMetrics(metrics.filter((m) => m.key !== metricKey));
        showAlert('success', 'Metric deleted!');
      } catch (err) {
        console.error(err);
        showAlert('fail', 'Failed to delete metric');
      } finally {
        setLoading(false);
      }
    });
  };

  const onToggleMetricActive = async (metric) => {
    setLoading(true);
    try {
      await updateMetric(configId, metric.key, { is_active: !metric.is_active });
      setMetrics(
        metrics.map((m) => (m.key === metric.key ? { ...m, is_active: !m.is_active } : m)),
      );
      showAlert('success', 'Metric updated!');
    } catch (err) {
      console.error(err);
      showAlert('fail', 'Failed to update metric');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (metric) => {
    if (editingMetricKey === metric.key) {
      setEditingMetricKey(null);
      return;
    }
    setShowNewMetricForm(false);
    setEditingMetricKey(metric.key);
  };

  if (loading) return <PageLoader />;

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row">
        {/* Main Settings */}
        <div className="col-6 radius-yes p-2 bg-yes">
          <div className="d-flex justify-between align-center">
            <h2 className="h2-title">{name}</h2>

            {!isNew && (
              <div className="buttons-container d-flex justify-end">
                <Button
                  variant="secondary"
                  size="large"
                  onClick={() =>
                    navigate(
                      `/organizations/${organizationId}/post-call-analysis/${configId}/analytics`,
                    )
                  }
                >
                  Analytics
                </Button>
              </div>
            )}
          </div>

          <div className="int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr">
            <div className="input-block d-flex flex-row justify-between">
              <div className="input-description">
                <div className="input-title">Configuration name</div>
                <div className="small-font">Enter configuration name</div>
              </div>
              <div className="align-wrapper d-flex align-center">
                <CustomInput
                  id="config-name"
                  label="Name"
                  placeholder="Enter name"
                  type="text"
                  value={name}
                  onChange={(id, value) => setName(value)}
                  disabled={isDisabled}
                />
              </div>
            </div>

            <div className="input-block d-flex flex-row justify-between">
              <div className="input-description">
                <div className="input-title">Description</div>
                <div className="small-font">Enter description</div>
              </div>
              <div className="align-wrapper d-flex align-center">
                <CustomTextarea
                  id="description"
                  label="Description"
                  name="description"
                  type="text"
                  addedText={description}
                  onChange={(value) => setDescription(value)}
                  resizable={false}
                  disabled={isDisabled}
                  height="100px"
                />
              </div>
            </div>

            <div className="input-block d-flex flex-row justify-between">
              <div className="input-description">
                <div className="input-title">Thread Filter</div>
                <div className="small-font">Enter thread filter</div>
              </div>
              <div className="align-wrapper d-flex align-center">
                <CustomTextarea
                  id="threadFilter"
                  label="Thread Filter"
                  name="threadFilter"
                  type="text"
                  addedText={threadFilter}
                  onChange={(value) => setThreadFilter(value)}
                  resizable={false}
                  disabled={isDisabled}
                  height="100px"
                />
              </div>
            </div>

            <div className="input-row input-block d-flex flex-row justify-between">
              <div className="input-description">
                <div className="input-title">Use Context Message Time</div>
                <div className="small-font">Pass message creation timestamp</div>
              </div>
              <div className="align-wrapper d-flex align-center">
                <CustomToggle
                  id="toggle-message-time"
                  label={contextMessageTime ? 'Active' : 'Inactive'}
                  checked={contextMessageTime}
                  onChange={(id, value) => setContextMessageTime(value)}
                  disabled={isDisabled}
                />
              </div>
            </div>

            <div className="input-block d-flex flex-row justify-between">
              <div className="input-description">
                <div className="input-title">Assistant</div>
                <div className="small-font">Select assistant for analysis</div>
              </div>
              <div className="align-wrapper d-flex align-center">
                <CustomSelect
                  id="assistant-select"
                  label="Assistant"
                  options={agents.map((a) => ({
                    value: a.key,
                    label: a.name,
                  }))}
                  value={assistantKey}
                  multiSelect={false}
                  onChange={(val) => setAssistantKey(val)}
                  disabled={isDisabled}
                />
              </div>
            </div>

            <div className="input-block d-flex flex-row justify-between">
              <div className="input-description">
                <div className="input-title">Channels</div>
                <div className="small-font">Select channels to analyze</div>
              </div>
              <div className="align-wrapper d-flex align-center">
                <CustomSelect
                  id="channels-select"
                  label="Channels"
                  options={channelsList.map((c) => ({
                    value: c.id,
                    label: c.name,
                  }))}
                  value={selectedChannelIds}
                  multiSelect={true}
                  onChange={(val) => setSelectedChannelIds(val)}
                  disabled={isDisabled}
                />
              </div>
            </div>

            {isNew && (
              <div className="input-row input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Include standard metrics</div>
                  <div className="small-font">Pre-populate with standard analysis metrics</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomToggle
                    id="toggle-standard-metrics"
                    label={includeStandardMetrics ? 'Yes' : 'No'}
                    checked={includeStandardMetrics}
                    onChange={(id, value) => setIncludeStandardMetrics(value)}
                    disabled={isDisabled}
                  />
                </div>
              </div>
            )}

            {!isNew && (
              <div className="input-row input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Active</div>
                  <div className="small-font">Enable or disable this configuration</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomToggle
                    id="toggle-active"
                    label={isActive ? 'Active' : 'Inactive'}
                    checked={isActive}
                    onChange={(id, value) => setIsActive(value)}
                    disabled={isDisabled}
                  />
                </div>
              </div>
            )}

            <div className="buttons-container justify-end gap-12">
              <Button background="color2" size="medium" disabled={isDisabled} onClick={onSave}>
                Save
              </Button>
              <Button variant="danger" size="medium" onClick={onCancel}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Metrics Section - Only show for existing configs */}
      {!isNew && (
        <div className="row mt-2">
          <div className="col-6 radius-yes p-2 bg-yes">
            <h3 className="h3-title">Metrics</h3>
            <div className="small-font mb-2">Define the metrics to extract from conversations</div>

            {metrics.map((metric) => (
              <div key={metric.key}>
                {editingMetricKey === metric.key ? (
                  <MetricForm
                    initialData={metric}
                    onSubmit={(data) => onUpdateMetric(metric.key, data)}
                    onCancel={() => setEditingMetricKey(null)}
                    isDisabled={isDisabled}
                    showAlert={showAlert}
                  />
                ) : (
                  <div
                    className="metric-row mb-2 p-2 border-yes radius-yes"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleEditClick(metric)}
                  >
                    <div className="d-flex flex-row justify-between align-center mb-1">
                      <div className="d-flex flex-column">
                        <div className="input-title">{metric.display_name}</div>
                        <div className="small-font">
                          {metric.is_standard ? 'Standard metric' : 'Custom metric'} •{' '}
                          {metric.metric_type}
                          {metric.metric_type === 'numeric' && (
                            <> • {metric.constraints?.nullable ? 'nullable' : 'not nullable'}</>
                          )}
                        </div>
                      </div>
                      <div className="d-flex align-center gap">
                        <div onClick={(e) => e.stopPropagation()}>
                          <CustomToggle
                            id={`metric-active-${metric.key}`}
                            label={metric.is_active ? 'Active' : 'Inactive'}
                            checked={metric.is_active}
                            onChange={() => onToggleMetricActive(metric)}
                            disabled={isDisabled}
                          />
                        </div>
                        {!metric.is_standard && !isDisabled && (
                          <div onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="danger"
                              size="small"
                              onClick={() => onDeleteMetric(metric.key, metric.is_standard)}
                            >
                              <i className="fa-solid fa-trash"></i>
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>

                    {metric.description && (
                      <div
                        className="metric-description-text small-font"
                        style={{ color: '#666', marginTop: '4px' }}
                      >
                        {metric.description}
                      </div>
                    )}

                    {metric.constraints?.values && metric.constraints.values.length > 0 && (
                      <div
                        className="metric-allowed-values small-font"
                        style={{ color: '#666', marginTop: '4px' }}
                      >
                        <span style={{ fontWeight: 500 }}>Allowed values: </span>
                        {metric.constraints.values.join(', ')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}

            {!showNewMetricForm ? (
              !isDisabled && (
                <Button
                  variant="secondary"
                  size="medium"
                  onClick={() => {
                    setEditingMetricKey(null);
                    setShowNewMetricForm(true);
                  }}
                >
                  <i className="fa-solid fa-plus"></i> Add Custom Metric
                </Button>
              )
            ) : (
              <MetricForm
                onSubmit={onAddMetric}
                onCancel={() => setShowNewMetricForm(false)}
                isDisabled={isDisabled}
                showAlert={showAlert}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PostCallAnalysisDetailWrapper({ canEdit = true }) {
  return (
    <ConfirmationProvider>
      <PostCallAnalysisApiProvider>
        <AgentApiProvider>
          <ChannelApiProvider>
            <PostCallAnalysisDetail canEdit={canEdit} />
          </ChannelApiProvider>
        </AgentApiProvider>
      </PostCallAnalysisApiProvider>
    </ConfirmationProvider>
  );
}

export default PostCallAnalysisDetailWrapper;
